import React, { useEffect, useState } from 'react';
import { Box, Checkbox, Tooltip, Button, Typography } from '@mui/material';
import FieldDetails from './FieldDetails';
import { Table } from '../../components/table/Table';
import * as productivityService from '../../services/productivity-service';
import { normalizeToRange, getRounded } from '../../lib/numbers';
import { getFormattedDate } from '../../lib/dates';
import { generateCsvDownload } from '../../lib/csv';
import { usePermission } from '../../hooks/use-permission';
import { useAlerts } from '../../hooks/use-alerts';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { RunRxButton } from '../run-rx/RunRxButton';

export default function FieldProductivity(props) {
  const {
    fields,
    getFieldProductivity,
    growerId,
    isLoading,
    selectedFields,
    setApproval,
  } = props;
  const { showInfo, showError } = useAlerts();
  const [isConfirmingRemoval, setConfirmingRemoval] = useState(false);
  const [isConfirmingNitrogenLossRemoval, setConfirmingNitrogenLossRemoval] =
    useState(false);
  const canRun = usePermission('RUN_RX');
  const canRemove = usePermission('ADMIN_PRODUCTIVITY');
  const [showStats, setShowStats] = useState(false);
  const [statsField, setStatsField] = useState(null);

  useEffect(() => {
    getFieldProductivity(growerId);
  }, [getFieldProductivity, growerId]);

  const hideStats = () => {
    setShowStats(false);
    setStatsField(null);
  };

  const handleApprovalChange = (field) => {
    setApproval(field, !field.isApproved);
  };

  const downloadCsv = () => {
    const filename = `productivity-export-${growerId}.csv`;

    let csvArr = fields;
    if (selectedFields && selectedFields.length > 0) {
      const selectedAAIDs = selectedFields
        ? selectedFields.map((f) => f.aaManagedId.toUpperCase())
        : [];
      csvArr = fields.filter((f) =>
        selectedAAIDs.includes(f.aaManagedId.toUpperCase()),
      );
    }

    const csvDataPpNlStatusColumns = csvArr.map((f) => ({
      field_name: f.fieldName,
      farm_name: f.farmName,
      field_id: f.fieldId,
      acres: getRounded(f.acres, 2),
      has_productivity: f.hasProductivity ? 'Yes' : 'No',
      pp_processed_date: getFormattedDate(f.processedPPDate) || 'N/A',
      has_nitrogen_loss: f.hasNitrogenLoss ? 'Yes' : 'No',
      nl_processed_date: getFormattedDate(f.processedNLDate) || 'N/A',
      approved_yld: (f.acceptedyieldstr || '').toString().replace(/,|\n/g, ' '),
      yld_used: (f.usedyieldstr || '').toString().replace(/,|\n/g, ' '),
      // eslint-disable-next-line no-nested-ternary
      approved: f.hasProductivity ? (f.isApproved ? 'Yes' : 'No') : 'N/A',
      avg_productivity: normalizeToRange(f.avgProductivity, 5),
      aaid: f.aaManagedId,
    }));

    generateCsvDownload(csvDataPpNlStatusColumns, filename);
  };

  const onAdjustClick = (event, field) => {
    event.stopPropagation();
    event.preventDefault();

    setStatsField(field);
    setShowStats(true);
  };

  return (
    <Box sx={{ height: '100%' }}>
      {isConfirmingRemoval ? (
        <ConfirmationDialog
          title="Remove Productivity"
          message="Are you sure you want to remove productivity for the selected fields?"
          handleClose={() => setConfirmingRemoval(false)}
          handleConfirm={removeProductivity}
        />
      ) : null}
      {isConfirmingNitrogenLossRemoval ? (
        <ConfirmationDialog
          title="Remove Nitrogen Loss"
          message="Are you sure you want to remove nitrogen loss for the selected fields?"
          handleClose={() => setConfirmingNitrogenLossRemoval(false)}
          handleConfirm={removeNitrogenLoss}
        />
      ) : null}

      <Table
        fields={fields}
        allowContractSelection={true}
        columns={buildColumns()}
        actions={buildActions()}
        loading={isLoading}
        headerHeight={75}
        getRowHeight={() => 'auto'}
      />

      {showStats && statsField && (
        <FieldDetails field={statsField} handleClose={hideStats} />
      )}
    </Box>
  );

  function buildRunRxButton() {
    return <RunRxButton key="run" />;
  }

  function buildActions() {
    return [
      canRun && buildRunRxButton(),
      <Button key="csv" onClick={downloadCsv}>
        Export CSV
      </Button>,
      canRemove && (
        <Button
          key="remove"
          disabled={!selectedFields.length}
          onClick={() => setConfirmingRemoval(true)}
        >
          Remove Productivity
        </Button>
      ),
      canRemove && (
        <Button
          key="remove-nitrogen-loss"
          disabled={!selectedFields.length}
          onClick={() => setConfirmingNitrogenLossRemoval(true)}
        >
          Remove Nitrogen Loss
        </Button>
      ),
    ].filter(Boolean);
  }

  async function removeProductivity() {
    setConfirmingRemoval(false);
    showInfo('Productivity removal has been scheduled');

    await productivityService
      .remove(
        growerId,
        selectedFields.map((f) => f.aaManagedId),
      )
      .catch((e) => showError(e));
  }

  async function removeNitrogenLoss() {
    setConfirmingNitrogenLossRemoval(false);
    showInfo('Nitrogen loss removal has been scheduled');

    await productivityService
      .removeNitrogenLoss(
        growerId,
        selectedFields.map((f) => f.aaManagedId),
      )
      .catch((e) => showError(e));
  }

  function normalizeStatus(params) {
    const { value } = params;
    if (/(complete|generated)/i.test(value)) return 'Complete';
  }

  function buildColumns() {
    return [
      {
        field: 'plannedCrop',
        headerName: 'Planned Crop',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'processedPPStatus',
        headerName: 'Productivity Status',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueFormatter: normalizeStatus,
      },
      {
        field: 'processedPPDate',
        headerName: 'Productivity Date',
        headerAlign: 'center',
        align: 'center',
        type: 'date',
        flex: 1,
        valueFormatter: ({ value }) => getFormattedDate(value) || '--',
      },
      {
        field: 'processedNLStatus',
        headerName: 'NL Status',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueFormatter: ({ value }) => {
          if (/(complete|generated)/i.test(value)) return 'Complete';
        },
      },
      {
        field: 'processedNLDate',
        headerName: 'NL Date',
        headerAlign: 'center',
        align: 'center',
        type: 'date',
        flex: 1,
        valueFormatter: ({ value }) => getFormattedDate(value) || '--',
      },
      {
        field: 'acceptedyieldstr',
        headerName: 'Approved Yield',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        minWidth: 100,
        renderCell: ({ row }) => (
          <Box>
            {(row.acceptedyieldstr || []).map((yieldString, i) => (
              <Typography
                sx={{ fontSize: '.875rem', textAlign: 'center' }}
                key={`acceptedyield_${i + 1}`}
              >
                {yieldString}
              </Typography>
            ))}
          </Box>
        ),
      },
      {
        field: 'usedyieldstr',
        headerName: 'Yield Used for Productivity',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        minWidth: 100,
        renderCell: ({ row }) => (
          <Box>
            {(row.usedyieldstr || []).map((yieldString, i) => (
              <Typography
                sx={{ fontSize: '.875rem', textAlign: 'center' }}
                key={`usedyield_${i + 1}`}
              >
                {yieldString}
              </Typography>
            ))}
          </Box>
        ),
      },
      {
        field: 'isApproved',
        headerName: 'Approved',
        align: 'center',
        headerAlign: 'center',
        type: 'boolean',
        disableColumnMenu: true,
        flex: 1,
        renderCell: ({ row }) =>
          row.hasProductivity || row.hasNitrogenLoss ? (
            <Checkbox
              color="primary"
              disabled={!canRun}
              checked={row.isApproved}
              onChange={() => handleApprovalChange(row)}
              sx={{ display: 'flex', flexDirection: 'row' }}
            />
          ) : null,
      },
      {
        field: 'avgProductivity',
        headerName: 'Avg. Productivity',
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.7,
        valueFormatter: ({ value }) => normalizeToRange(value, 5),
      },
      {
        field: 'statistics',
        headerName: 'Stats',
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        renderCell: ({ row }) => (
          <Tooltip
            title={'Unavailable'}
            disableHoverListener={
              !!row.hasProductivity || !!row.hasNitrogenLoss
            }
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <span>
              <Button
                variant="text"
                onClick={(event) => onAdjustClick(event, row)}
                disabled={
                  !canRun || (!row.hasProductivity && !row.hasNitrogenLoss)
                }
              >
                Show Stats
              </Button>
            </span>
          </Tooltip>
        ),
      },
    ].filter(Boolean);
  }
}
